import React, { useState, useEffect } from 'react'
import { Settings, Save, RefreshCw, Key, Globe, Database, AlertCircle, Eye, EyeOff, Copy, Check } from 'lucide-react'
import { toast } from 'react-hot-toast'

const ApiSettings = () => {
  const [apiConfig, setApiConfig] = useState({
    supabaseUrl: '',
    supabaseAnonKey: '',
    devrevApiUrl: '',
    devrevApiKey: ''
  })

  const [showSecrets, setShowSecrets] = useState({
    supabaseAnonKey: false,
    devrevApiKey: false
  })

  const [copiedField, setCopiedField] = useState(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Load existing configuration on component mount
  useEffect(() => {
    loadApiConfiguration()
  }, [])

  const loadApiConfiguration = () => {
    setIsLoading(true)
    try {
      // Load from localStorage or API
      const savedConfig = localStorage.getItem('apiConfiguration')
      if (savedConfig) {
        const config = JSON.parse(savedConfig)
        setApiConfig(config)
      } else {
        // Load default configuration
        loadDefaultConfiguration()
      }
    } catch (error) {
      console.error('Error loading API configuration:', error)
      toast.error('Failed to load API configuration')
    } finally {
      setIsLoading(false)
    }
  }

  const loadDefaultConfiguration = () => {
    // Set default values with actual Supabase and DevRev defaults
    setApiConfig({
      supabaseUrl: 'https://iijqpznihnlpmsjtboyq.supabase.co',
      supabaseAnonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlpanFwem5paG5scG1zanRib3lxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU2OTA5MTEsImV4cCI6MjA3MTI2NjkxMX0.Tf7IhhZHcAEIuIVJyJk4TnGYII-7PccCPvRU0ILkY_c',
      devrevApiUrl: 'https://api.devrev.ai',
      devrevApiKey: 'your-devrev-application-access-token'
    })
  }

  const handleInputChange = (field, value) => {
    setApiConfig(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      toast.success('Copied to clipboard!')
      setTimeout(() => setCopiedField(null), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Save to localStorage
      localStorage.setItem('apiConfiguration', JSON.stringify(apiConfig))
      
      // Here you can also save to your backend API
      // await saveApiConfiguration(apiConfig)
      
      toast.success('API configuration saved successfully!')
    } catch (error) {
      console.error('Error saving API configuration:', error)
      toast.error('Failed to save API configuration')
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestConnection = async (apiType) => {
    try {
      // Test connection logic here
      toast.success(`${apiType} connection test successful!`)
    } catch (error) {
      toast.error(`${apiType} connection test failed!`)
    }
  }

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all API settings to default values?')) {
      loadDefaultConfiguration()
      toast.success('API settings reset to defaults')
    }
  }

  const apiConfigurations = [
    {
      title: 'Supabase Configuration',
      description: 'Database and authentication settings',
      icon: Database,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      fields: [
        { key: 'supabaseUrl', label: 'Supabase URL', placeholder: 'https://your-project.supabase.co' },
        { key: 'supabaseAnonKey', label: 'Supabase Anon Key', placeholder: 'your-supabase-anon-key', type: 'password' }
      ]
    },
    {
      title: 'DevRev API',
      description: 'Customer support and ticketing system',
      icon: Globe,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      fields: [
        { key: 'devrevApiUrl', label: 'DevRev API URL', placeholder: 'https://api.devrev.ai' },
        { key: 'devrevApiKey', label: 'DevRev Application Access Token', placeholder: 'your-devrev-application-access-token', type: 'password' }
      ]
    }
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading API configuration...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">API Settings</h1>
        <p className="text-gray-600">Configure API endpoints and authentication keys for all integrated services</p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
          <button
            onClick={handleReset}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Reset to Defaults</span>
          </button>
        </div>
      </div>

      {/* API Configuration Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {apiConfigurations.map((config, index) => {
          const Icon = config.icon
          return (
            <div key={index} className={`card ${config.bgColor}`}>
              <div className="flex items-center mb-4">
                <div className={`${config.color} mr-3`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{config.title}</h3>
                  <p className="text-sm text-gray-600">{config.description}</p>
                </div>
              </div>

              <div className="space-y-4">
                {config.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    <div className="flex space-x-2">
                      <div className="flex-1 relative">
                        <input
                          type={field.type === 'password' && !showSecrets[field.key] ? 'password' : 'text'}
                          value={apiConfig[field.key]}
                          onChange={(e) => handleInputChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="input w-full pr-20"
                        />
                        {field.type === 'password' && (
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(field.key)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            title={showSecrets[field.key] ? 'Hide' : 'Show'}
                          >
                            {showSecrets[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => copyToClipboard(apiConfig[field.key], field.key)}
                        className="btn-secondary px-3 py-2 text-sm"
                        title="Copy to clipboard"
                      >
                        {copiedField === field.key ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleTestConnection(config.title)}
                        className="btn-secondary px-3 py-2 text-sm"
                        title="Test Connection"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Configuration Status */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {apiConfigurations.map((config, index) => {
            const hasUrl = apiConfig[config.fields[0].key] && apiConfig[config.fields[0].key] !== ''
            const hasKey = apiConfig[config.fields[1].key] && apiConfig[config.fields[1].key] !== ''
            const isConfigured = hasUrl && hasKey
            
            return (
              <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                <div className={`w-3 h-3 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="text-sm font-medium text-gray-700">{config.title}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  isConfigured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {isConfigured ? 'Configured' : 'Not Configured'}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Important Notes */}
      <div className="card bg-yellow-50 border-yellow-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-yellow-800">Important Notes</h4>
            <ul className="text-sm text-yellow-700 mt-2 space-y-1">
              <li>• API keys are stored locally in your browser</li>
              <li>• Changes take effect immediately after saving</li>
              <li>• Test connections before saving to ensure proper configuration</li>
              <li>• Keep your API keys secure and don't share them</li>
              <li>• Default Supabase URL and key are pre-configured</li>
              <li>• DevRev Application Access Token needs to be configured</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ApiSettings
